<script setup>
// ============================================================================
// BonBoZ: bảng lọc theo khoảng ngày của danh sách hội thoại.
//
// Vì sao KHÔNG sửa thẳng `ui/DatePicker/DatePicker.vue`: bảng đó đang được bốn
// màn Báo cáo dùng chung. Nhét mốc nhanh, thanh trượt giờ và nút chọn kiểu ngày
// vào đó là đổi cả Báo cáo theo.
//
// Nhưng LƯỚI NGÀY thì dùng lại của Chatwoot (`CalendarWeek` / `CalendarMonth` /
// `CalendarYear`). Vẽ lại lưới lịch chỉ để trông y hệt là việc thừa, và mỗi lần
// Chatwoot sửa lịch thì bản chép tay lại lệch thêm một nấc.
//
// Bố cục đổi theo bề ngang bằng CSS chứ không bằng JS:
//   dưới 768px — mốc nhanh thành hàng ngang cuộn được, MỘT tháng
//   từ 768px   — mốc nhanh thành cột trái có tiêu đề nhóm, HAI tháng
// ============================================================================
import { computed, ref, watchEffect } from 'vue';
import {
  addMinutes,
  endOfDay,
  format,
  isSameMonth,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
  addMonths,
} from 'date-fns';
import {
  CALENDAR_TYPES,
  CALENDAR_PERIODS,
  moveCalendarDate,
} from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import CalendarWeek from 'dashboard/components/ui/DatePicker/components/CalendarWeek.vue';
import CalendarMonth from 'dashboard/components/ui/DatePicker/components/CalendarMonth.vue';
import CalendarYear from 'dashboard/components/ui/DatePicker/components/CalendarYear.vue';
import { DATE_PRESET_GROUPS } from './presets';
import { DATE_FILTER_MODES } from 'dashboard/composables/useDateFilterMode';

const props = defineProps({
  range: { type: Array, default: () => [] },
  mode: { type: String, default: DATE_FILTER_MODES.UPDATED },
  timeRange: { type: Array, default: () => [] },
});

const emit = defineEmits(['apply', 'clear', 'close']);

const { START_CALENDAR } = CALENDAR_TYPES;
const { WEEK, MONTH, YEAR } = CALENDAR_PERIODS;

const MINUTES_IN_DAY = 24 * 60;
// Bước 15 phút: đủ mịn cho ca trực, mà kéo tay vẫn dừng đúng mốc tròn.
const MINUTE_STEP = 15;
const INPUT_FORMAT = 'dd/MM/yyyy HH:mm';

const MODES = [
  { value: DATE_FILTER_MODES.CREATED, label: 'BONBOZ.LOC_NGAY.NGAY_TAO' },
  { value: DATE_FILTER_MODES.UPDATED, label: 'BONBOZ.LOC_NGAY.NGAY_CAP_NHAT' },
];

// Hàng ngang trên điện thoại không có chỗ cho tiêu đề nhóm nên trải phẳng.
const flatPresets = DATE_PRESET_GROUPS.flatMap(group => group.items);

const today = new Date();

const selectedStartDate = ref(startOfDay(props.range[0] || today));
const selectedEndDate = ref(endOfDay(props.range[1] || today));
const selectingEndDate = ref(false);
const hoveredEndDate = ref(null);

const startCurrentDate = ref(startOfMonth(selectedStartDate.value));
const endCurrentDate = ref(
  isSameMonth(selectedStartDate.value, selectedEndDate.value)
    ? startOfMonth(addMonths(selectedStartDate.value, 1))
    : startOfMonth(selectedEndDate.value)
);

const views = ref({ start: WEEK, end: WEEK });
const activePreset = ref('');

const startMinutes = ref(props.timeRange[0] ?? 0);
const endMinutes = ref(props.timeRange[1] ?? MINUTES_IN_DAY);
const dateMode = ref(props.mode);

const asMinuteLabel = minutes => {
  const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mm = String(minutes % 60).padStart(2, '0');
  return `${hh}:${mm}`;
};

// 24:00 nghĩa là "hết ngày", không phải 00:00 hôm sau — lùi về mốc cuối cùng
// còn nằm trong ngày để khoảng lọc không nuốt sang ngày kế tiếp.
const withTime = (day, minutes) =>
  minutes >= MINUTES_IN_DAY
    ? endOfDay(day)
    : addMinutes(startOfDay(day), minutes);

const appliedFrom = computed(() =>
  withTime(selectedStartDate.value, startMinutes.value)
);
const appliedTo = computed(() =>
  withTime(selectedEndDate.value || selectedStartDate.value, endMinutes.value)
);

const startText = ref('');
const endText = ref('');
watchEffect(() => {
  startText.value = format(appliedFrom.value, INPUT_FORMAT);
  endText.value = format(appliedTo.value, INPUT_FORMAT);
});

// Gõ tay để nhảy tới ngày xa — bấm mũi tên qua mười mấy tháng thì không ai chịu
// nổi. Gõ sai thì trả về giá trị cũ chứ không báo lỗi: ô này không phải bài thi.
const readTypedDate = (text, apply) => {
  const parsed = parse(text.trim(), INPUT_FORMAT, new Date());
  if (!isValid(parsed)) {
    startText.value = format(appliedFrom.value, INPUT_FORMAT);
    endText.value = format(appliedTo.value, INPUT_FORMAT);
    return;
  }
  activePreset.value = '';
  apply(parsed);
};

const onTypedStart = () =>
  readTypedDate(startText.value, parsed => {
    selectedStartDate.value = startOfDay(parsed);
    startCurrentDate.value = startOfMonth(parsed);
    startMinutes.value = parsed.getHours() * 60 + parsed.getMinutes();
    selectingEndDate.value = false;
  });

const onTypedEnd = () =>
  readTypedDate(endText.value, parsed => {
    selectedEndDate.value = endOfDay(parsed);
    endCurrentDate.value = startOfMonth(parsed);
    endMinutes.value = parsed.getHours() * 60 + parsed.getMinutes();
    selectingEndDate.value = false;
  });

const selectDate = day => {
  activePreset.value = '';
  if (!selectingEndDate.value || day < selectedStartDate.value) {
    selectedStartDate.value = day;
    selectedEndDate.value = null;
    selectingEndDate.value = true;
    return;
  }
  selectedEndDate.value = day;
  selectingEndDate.value = false;
};

const applyPreset = preset => {
  const [from, to] = preset.range();
  activePreset.value = preset.key;
  selectedStartDate.value = from;
  selectedEndDate.value = to;
  selectingEndDate.value = false;
  startCurrentDate.value = startOfMonth(from);
  endCurrentDate.value = isSameMonth(from, to)
    ? startOfMonth(addMonths(from, 1))
    : startOfMonth(to);
};

// Ở lưới NGÀY, mũi tên nhảy một tháng; ở lưới THÁNG nó nhảy một năm. Lưới NĂM
// tự lo lấy thập kỷ của nó.
const navigate = (calendarType, direction) => {
  const period = views.value[calendarType] === WEEK ? MONTH : YEAR;
  const { start, end } = moveCalendarDate(
    calendarType,
    startCurrentDate.value,
    endCurrentDate.value,
    direction,
    period
  );
  startCurrentDate.value = start;
  endCurrentDate.value = end;
};

const setView = (calendarType, view) => {
  views.value[calendarType] = view;
};

const openCalendar = (index, calendarType, period) => {
  const current =
    calendarType === START_CALENDAR
      ? startCurrentDate.value
      : endCurrentDate.value;
  const next =
    period === MONTH
      ? new Date(current.getFullYear(), index, 1)
      : new Date(index, current.getMonth(), 1);

  if (calendarType === START_CALENDAR) startCurrentDate.value = next;
  else endCurrentDate.value = next;

  setView(calendarType, period === MONTH ? WEEK : MONTH);
};

const onStartMinutes = event => {
  startMinutes.value = Math.min(Number(event.target.value), endMinutes.value);
};

const onEndMinutes = event => {
  endMinutes.value = Math.max(Number(event.target.value), startMinutes.value);
};

const sliderFill = computed(() => ({
  left: `${(startMinutes.value / MINUTES_IN_DAY) * 100}%`,
  right: `${100 - (endMinutes.value / MINUTES_IN_DAY) * 100}%`,
}));

const canApply = computed(
  () => isValid(selectedStartDate.value) && isValid(appliedTo.value)
);

const onApply = () => {
  if (!canApply.value) return;
  emit('apply', {
    from: appliedFrom.value,
    to: appliedTo.value,
    mode: dateMode.value,
    range: [selectedStartDate.value, selectedEndDate.value],
    timeRange: [startMinutes.value, endMinutes.value],
  });
};
</script>

<template>
  <div
    class="flex flex-col h-full md:h-auto md:flex-row bg-n-solid-2 md:rounded-xl overflow-hidden"
  >
    <!-- Đầu bảng CHỈ có trên điện thoại: bảng chiếm trọn màn nên phải có đường
         thoát ra, bấm nền không còn là đường thoát nữa. -->
    <div
      class="flex items-center justify-between px-4 h-14 border-b border-n-weak md:hidden"
    >
      <span class="text-base font-medium text-n-slate-12">
        {{ $t('BONBOZ.LOC_NGAY.TIEU_DE') }}
      </span>
      <button
        type="button"
        class="grid rounded-lg size-9 place-items-center text-n-slate-11 hover:bg-n-alpha-1"
        :aria-label="$t('BONBOZ.LOC_NGAY.DONG')"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-5" aria-hidden="true" />
      </button>
    </div>

    <!-- Mốc nhanh, bản điện thoại: một hàng ngang cuộn được -->
    <div
      class="flex gap-2 px-4 py-3 overflow-x-auto border-b border-n-weak md:hidden"
    >
      <button
        v-for="preset in flatPresets"
        :key="preset.key"
        type="button"
        class="px-3 py-1.5 text-sm rounded-full whitespace-nowrap shrink-0 transition-colors"
        :class="
          activePreset === preset.key
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-1 text-n-slate-11'
        "
        @click="applyPreset(preset)"
      >
        {{ $t(preset.label) }}
      </button>
    </div>

    <!-- Mốc nhanh, bản máy tính: cột trái có tiêu đề nhóm -->
    <aside
      class="hidden md:flex md:flex-col md:w-44 md:shrink-0 md:gap-3 md:py-4 md:px-3 md:border-r md:border-n-weak md:max-h-[520px] md:overflow-y-auto"
    >
      <div
        v-for="group in DATE_PRESET_GROUPS"
        :key="group.key"
        class="flex flex-col gap-0.5"
      >
        <span class="px-2 text-xs font-medium text-n-slate-10">
          {{ $t(group.label) }}
        </span>
        <button
          v-for="preset in group.items"
          :key="preset.key"
          type="button"
          class="px-2 py-1.5 text-sm rounded-lg text-start transition-colors"
          :class="
            activePreset === preset.key
              ? 'bg-n-brand/[0.12] text-n-brand font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="applyPreset(preset)"
        >
          {{ $t(preset.label) }}
        </button>
      </div>
    </aside>

    <div class="flex flex-col flex-1 min-h-0 md:w-[700px]">
      <div class="flex gap-2 px-4 pt-4 pb-3">
        <label class="flex-1 min-w-0">
          <span class="sr-only">{{ $t('BONBOZ.LOC_NGAY.TU_NGAY') }}</span>
          <input
            v-model="startText"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="onTypedStart"
          />
        </label>
        <label class="flex-1 min-w-0">
          <span class="sr-only">{{ $t('BONBOZ.LOC_NGAY.DEN_NGAY') }}</span>
          <input
            v-model="endText"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="onTypedEnd"
          />
        </label>
      </div>

      <div class="flex-1 min-h-0 px-4 overflow-y-auto">
        <div class="flex gap-6">
          <div class="flex-1 min-w-0">
            <CalendarWeek
              v-if="views.start === WEEK"
              calendar-type="start"
              :current-date="today"
              :start-current-date="startCurrentDate"
              :end-current-date="endCurrentDate"
              :selected-start-date="selectedStartDate"
              :selected-end-date="selectedEndDate"
              :selecting-end-date="selectingEndDate"
              :hovered-end-date="hoveredEndDate"
              @select-date="selectDate"
              @update-hovered-end-date="hoveredEndDate = $event"
              @prev="navigate('start', 'prev')"
              @next="navigate('start', 'next')"
              @set-view="setView"
            />
            <CalendarMonth
              v-else-if="views.start === MONTH"
              calendar-type="start"
              :start-current-date="startCurrentDate"
              :end-current-date="endCurrentDate"
              @select-month="openCalendar($event, 'start', MONTH)"
              @prev="navigate('start', 'prev')"
              @next="navigate('start', 'next')"
              @set-view="setView"
            />
            <CalendarYear
              v-else
              calendar-type="start"
              :start-current-date="startCurrentDate"
              :end-current-date="endCurrentDate"
              @select-year="openCalendar($event, 'start', YEAR)"
            />
          </div>

          <!-- Tháng thứ hai: máy tính mới có chỗ. Lưới ngày rộng cứng nên trên
               điện thoại nó đẩy cả bảng tràn ra ngoài màn. -->
          <div class="hidden md:block md:flex-1 md:min-w-0">
            <CalendarWeek
              v-if="views.end === WEEK"
              calendar-type="end"
              :current-date="today"
              :start-current-date="startCurrentDate"
              :end-current-date="endCurrentDate"
              :selected-start-date="selectedStartDate"
              :selected-end-date="selectedEndDate"
              :selecting-end-date="selectingEndDate"
              :hovered-end-date="hoveredEndDate"
              @select-date="selectDate"
              @update-hovered-end-date="hoveredEndDate = $event"
              @prev="navigate('end', 'prev')"
              @next="navigate('end', 'next')"
              @set-view="setView"
            />
            <CalendarMonth
              v-else-if="views.end === MONTH"
              calendar-type="end"
              :start-current-date="startCurrentDate"
              :end-current-date="endCurrentDate"
              @select-month="openCalendar($event, 'end', MONTH)"
              @prev="navigate('end', 'prev')"
              @next="navigate('end', 'next')"
              @set-view="setView"
            />
            <CalendarYear
              v-else
              calendar-type="end"
              :start-current-date="startCurrentDate"
              :end-current-date="endCurrentDate"
              @select-year="openCalendar($event, 'end', YEAR)"
            />
          </div>
        </div>

        <!-- Giờ trong ngày, áp cho CẢ hai đầu khoảng: "từ ngày 1 tới ngày 7,
             mỗi ngày tính từ 18:00 tới 22:00". -->
        <div class="flex items-center gap-3 py-4">
          <div class="relative flex items-center flex-1 h-6">
            <span class="absolute inset-x-0 h-1 rounded-full bg-n-alpha-2" />
            <span
              class="absolute h-1 rounded-full bg-n-brand"
              :style="sliderFill"
            />
            <input
              :value="startMinutes"
              type="range"
              class="bonboz-truot-gio"
              :min="0"
              :max="MINUTES_IN_DAY"
              :step="MINUTE_STEP"
              :aria-label="$t('BONBOZ.LOC_NGAY.GIO_BAT_DAU')"
              @input="onStartMinutes"
            />
            <input
              :value="endMinutes"
              type="range"
              class="bonboz-truot-gio"
              :min="0"
              :max="MINUTES_IN_DAY"
              :step="MINUTE_STEP"
              :aria-label="$t('BONBOZ.LOC_NGAY.GIO_KET_THUC')"
              @input="onEndMinutes"
            />
          </div>
          <span
            class="text-sm tabular-nums text-n-slate-11 whitespace-nowrap w-[92px] text-end"
          >
            {{ asMinuteLabel(startMinutes) }} – {{ asMinuteLabel(endMinutes) }}
          </span>
        </div>
      </div>

      <div
        class="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-n-weak"
      >
        <div class="inline-flex gap-1 p-1 rounded-lg bg-n-alpha-2">
          <button
            v-for="item in MODES"
            :key="item.value"
            type="button"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="
              dateMode === item.value
                ? 'bg-n-solid-1 text-n-slate-12 font-medium'
                : 'text-n-slate-11'
            "
            @click="dateMode = item.value"
          >
            {{ $t(item.label) }}
          </button>
        </div>

        <button
          type="button"
          class="px-3 py-2 text-sm rounded-lg text-n-slate-11 hover:bg-n-alpha-1"
          @click="emit('clear')"
        >
          {{ $t('BONBOZ.LOC_NGAY.XOA') }}
        </button>

        <button
          type="button"
          class="px-5 py-2 ml-auto text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
          :disabled="!canApply"
          @click="onApply"
        >
          {{ $t('BONBOZ.LOC_NGAY.AP_DUNG') }}
        </button>
      </div>

      <p class="px-4 pb-3 m-0 text-xs leading-4 text-n-slate-10">
        {{ $t(`BONBOZ.LOC_NGAY.GIAI_THICH_${dateMode.toUpperCase()}`) }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Hai thanh trượt chồng lên nhau để thành một khoảng có hai đầu kéo. Thanh nền
   do phần tử khác vẽ, nên ở đây chỉ còn nút kéo là bắt được chuột. */
.bonboz-truot-gio {
  position: absolute;
  inset-inline: 0;
  width: 100%;
  height: 24px;
  margin: 0;
  appearance: none;
  background: transparent;
  pointer-events: none;
}

.bonboz-truot-gio::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  border: 2px solid rgb(var(--color-woot));
  background: rgb(var(--white));
  cursor: pointer;
  pointer-events: auto;
}

.bonboz-truot-gio::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  border: 2px solid rgb(var(--color-woot));
  background: rgb(var(--white));
  cursor: pointer;
  pointer-events: auto;
}
</style>
